import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { BookMarked, Tag, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase 3 · Lernsystem · anonymisierte freigegebene Drafts.
 * Admin-View: Liste durchsuchen, Detail ansehen, Stats sehen.
 */
export default function CampusPflegeKiLernsystem() {
  const { user } = useCampusAuth();
  const [cases, setCases] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterKind, setFilterKind] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterKind) params.append("kind", filterKind);
      if (filterTag) params.append("tag", filterTag);
      params.append("limit", "100");
      const [c, s] = await Promise.all([
        axios.get(`${API}/pflege-ki/lernsystem/cases?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/lernsystem/stats`, { withCredentials: true }),
      ]);
      setCases(c.data);
      setStats(s.data);
    } catch (e) {
      setCases({ count: 0, cases: [] });
    }
  }, [filterKind, filterTag]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/pflege-ki/lernsystem/case/${id}`, { withCredentials: true });
      setDetail(r.data);
    } catch { /* ignore */ }
  };

  if (!user?.is_admin) {
    return (
      <div className="rounded-3xl border border-cream-300 bg-white p-8">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">Admin only</div>
        <p className="text-sand-700">Diese Übersicht ist Admins vorbehalten.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lernsystem-page">
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-clay-dark">
          Admin · Pflege-KI Phase 3
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-sand-800 mt-1">
          Lernsystem · anonymisierte freigegebene Drafts
        </h1>
        <p className="text-sand-600 text-[14.5px] mt-2 max-w-3xl">
          Jeder freigegebene Pflege-KI-Entwurf wird automatisch anonymisiert (Namen, Daten, PLZ, Telefon, E-Mail entfernt)
          und hier gespeichert · für späteres KI-Training und als Referenzbibliothek für PDLs.
        </p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-3">
        <StatBox label="Gesamt-Lernfälle" value={stats?.total ?? 0} testId="lernsystem-stat-total" />
        <div className="rounded-2xl border border-cream-300 bg-cream-50 p-4" data-testid="lernsystem-by-kind">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2">Nach Dokumentart</div>
          <div className="flex flex-wrap gap-1.5">
            {(stats?.by_kind || []).map((b) => (
              <button key={b.kind}
                onClick={() => setFilterKind(filterKind === b.kind ? "" : b.kind)}
                className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11.5px] font-semibold transition-colors ${
                  filterKind === b.kind ? "bg-terra text-white" : "bg-white border border-cream-300 text-sand-700 hover:border-terra"
                }`}>
                {b.kind} · {b.count}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-cream-300 bg-cream-50 p-4" data-testid="lernsystem-top-tags">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2">Top-Tags</div>
          <div className="flex flex-wrap gap-1.5">
            {(stats?.top_tags || []).slice(0, 10).map((b) => (
              <button key={b.tag}
                onClick={() => setFilterTag(filterTag === b.tag ? "" : b.tag)}
                className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-semibold transition-colors ${
                  filterTag === b.tag ? "bg-sage-dark text-cream-50" : "bg-white border border-cream-300 text-sand-700 hover:border-sage-dark"
                }`}>
                <Tag className="h-3 w-3" strokeWidth={2} />
                {b.tag} · {b.count}
              </button>
            ))}
          </div>
        </div>
      </div>

      {(filterKind || filterTag) && (
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-sand-500">Aktive Filter:</span>
          {filterKind && (
            <button onClick={() => setFilterKind("")}
              className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-terra/15 text-terra text-[11.5px] font-semibold">
              kind={filterKind} <X className="h-3 w-3" strokeWidth={2} />
            </button>
          )}
          {filterTag && (
            <button onClick={() => setFilterTag("")}
              className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-sage-dark/15 text-sage-dark text-[11.5px] font-semibold">
              tag={filterTag} <X className="h-3 w-3" strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      {/* Cases List */}
      <div className="rounded-3xl border border-cream-300 bg-white p-6"
        data-testid="lernsystem-cases">
        <div className="flex items-center gap-2 mb-3">
          <BookMarked className="h-5 w-5 text-terra" strokeWidth={2} />
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra">
            {cases?.count || 0} Lernfälle
          </div>
        </div>
        <div className="space-y-2">
          {cases?.cases?.length ? cases.cases.map((c) => (
            <button key={c.id} onClick={() => openDetail(c.id)}
              data-testid={`lernsystem-case-${c.id.slice(0, 8)}`}
              className="w-full text-left rounded-xl border border-cream-300 bg-cream-50 p-3 hover:border-terra transition-colors">
              <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2 h-5 rounded-full bg-terra/15 text-terra text-[10.5px] font-bold uppercase tracking-[0.15em]">
                    {c.kind}
                  </span>
                  {(c.tags || []).slice(0, 4).map((t) => (
                    <span key={t} className="inline-flex items-center px-2 h-5 rounded-full bg-sage-dark/15 text-sage-dark text-[10.5px] font-semibold">
                      {t}
                    </span>
                  ))}
                </div>
                <span className="text-[11px] text-sand-500 font-mono">{c.created_at?.slice(0, 10)}</span>
              </div>
              <div className="text-[12.5px] text-sand-700 line-clamp-2">{c.output_preview}</div>
            </button>
          )) : (
            <div className="text-center py-8 text-sand-500 italic text-[13px]">
              Noch keine Lernfälle · Drafts via Haftungsmodal freigeben → werden automatisch hier landen.
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}>
          <div className="bg-white rounded-3xl border border-cream-300 max-w-4xl w-full p-6 shadow-2xl my-4"
            data-testid="lernsystem-detail">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra">
                  Lernfall · {detail.kind}
                </div>
                <h3 className="font-display text-xl text-sand-800 mt-1">
                  {detail.id?.slice(0, 8)} · {detail.created_at?.slice(0, 10)}
                </h3>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {(detail.tags || []).map((t) => (
                    <span key={t} className="inline-flex items-center px-2 h-5 rounded-full bg-sage-dark/15 text-sage-dark text-[10.5px] font-semibold">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <Button onClick={() => setDetail(null)} variant="outline" size="sm"
                className="h-9 rounded-full border-clay">Schließen</Button>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">Anonymisierter Output</div>
                <pre className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-sand-800 font-mono bg-cream-50 rounded-xl p-3 border border-cream-200 max-h-[400px] overflow-y-auto">
                  {detail.anon_output}
                </pre>
              </div>
              {detail.anon_input && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">Anonymisierter Input-Snapshot</div>
                  <pre className="whitespace-pre-wrap break-words text-[11.5px] leading-relaxed text-sand-700 font-mono bg-cream-50 rounded-xl p-3 border border-cream-200 max-h-[300px] overflow-y-auto">
                    {JSON.stringify(detail.anon_input, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-cream-300 bg-white p-4" data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-sand-500">{label}</div>
      <div className="font-display text-3xl text-sand-800 mt-1">{value}</div>
    </div>
  );
}
